import { processViewPartHtml, virtualDir } from "#skyward_global";
import { notifyDOMLoad } from "#skyward_window";
import { isDirty } from "./skyward.ui.changeTracking";
import { getUnencodedValue, getValue, setValue } from "./skyward.ui.controls";
import { ContainerPreSaveValidationResult, getPreSaveValidationHelper, PreSaveValidation, PreSaveValidationHelper, PreSaveValidationResult, PreSaveValidationSeverityType } from "./skyward.ui.dataobjectviewer.preSaveValidation";
import { registerEvents } from "./skyward.ui.events";
import { populate } from "./skyward.ui.multipleSelectBox";
import { JQueryInput } from "./types";

interface StandardFilter
{
    allowprocessinglists: boolean;
    relationshippath: string | null;
    skywardid: string;
    type: string;
    uniqueid: string;
    standardfilterindexes: number[];
}

interface CollapsePanelData
{
    loadAction: string;
    filterIndex: number;
    idPrefix: string;
    label: string;
    objectType: string;
    promptUniqueId: string;
    skywardId: string;
}

interface StandardFilterViewerData
{
    isRequired: boolean;
    loadAction: string;
    readonlyIds: string[];
    defaultPrompts: Record<string, unknown>;
}

interface BackingInputData
{
    allowadvancedfilter: boolean;
    searchmodulename: string;
    searchobjectname: string;
    standardfilters: StandardFilter[];
    values:
    {
        promptboundvalues: Record<string, unknown>;
        advancedfilter?: Record<string, string>;
    };
}

interface PerformValidationResult
{
    validation: PerformValidation[];
}

interface PerformValidation
{
    PromptUniqueID: string;
    ValidationDetails: PerformValidationDetails[];
}

interface PerformValidationDetails
{
    SeverityType: PreSaveValidationSeverityType;
    Message: string;
}

interface PromptUniqueID
{
    PromptUniqueID: string
    PromptUniqueIDHash: string
}

const skyward = window.skyward;

const classNames =
{
    advancedFilterUnbound: "advancedFilterUnbound",
    collapsePanel: "collapsePanel",
    collapsePanelContents: "collapsePanelContents",
    comboBoxSet: "comboBoxSet",
    dataSource: "dataSource",
    dmsBackingData: "dmsBackingData",
    filterProcessingListValue: "filterProcessingListValue",
    filterValue: "filterValue",
    filterViewValue: "filterViewValue",
    multiSelectBackingData: "multiSelectBackingData",
    newFilter: "newFilter",
    standardFilterAddButton: "standardFilterAddButton",
    standardFilterRemoveButton: "standardFilterRemoveButton",
    standardFilterViewer: "standardFilterViewer",
    standardFilterViewerUnbound: "standardFilterViewerUnbound",
    validationPrompt: "js-validatedPrompt",
    workflowContent: "workflowContent"
};

const events =
{
    initializeStandardFilterViewer,
    initializeStandardFilter,
    initializeAdvancedFilter
};

registerEvents("standardFilterViewer", events);

export function clear(standardfilter: JQuery<HTMLElement>)
{
    standardfilter.find(`.${classNames.filterValue}`).each(function ()
    {
        if (!(this as HTMLInputElement).readOnly)
        {
            setValue(this, null);
        }
    });

    removeCollapsePanels(standardfilter);
}

export function removeCollapsePanels(standardfilter: JQuery<HTMLElement>)
{
    standardfilter.find(`.${classNames.collapsePanel}`).each(function ()
    {
        const collapsePanel = $(this);
        const panelData = collapsePanel.data() as CollapsePanelData;
        if (panelData.filterIndex > 0)
        {
            removeCollapsePanel(collapsePanel);
        }
    });
}

function initializeStandardFilterViewer(this: JQueryInput)
{
    const container = $(this);
    container.removeClass(classNames.standardFilterViewerUnbound);

    const backingData = container.find(`.${classNames.dataSource}`);
    const data = JSON.parse(backingData.val() as string);
    data.values = data.values || {};
    data.values.promptboundvalues = data.values.promptboundvalues || {};
    backingData.data("currentValue", data);

    container.on("change", `.${classNames.filterViewValue}`, null, function ()
    {
        changeAdvancedFilterData(container);
    });
    container.on("change dirty", `.${classNames.filterValue}, .${classNames.filterProcessingListValue}`, null, function ()
    {
        changeFilterValue(this, container);
    });
    container.on("click", `.${classNames.standardFilterAddButton}`, null, addStandardFilterClick);
    container.on("click", `.${classNames.standardFilterRemoveButton}`, null, removeStandardFilterClick);
}

function initializeStandardFilter(this: JQueryInput)
{
    const standardFilter = $(this);
    standardFilter.removeClass(classNames.standardFilterViewerUnbound);
    standardFilter.on("change dirty", `.${classNames.filterValue}, .${classNames.filterProcessingListValue}`, null, skyward.promptsview.setModifiedNotification);
    standardFilter.each(skyward.promptsview.setModifiedNotification);

    initializeStandardFilterValidation(standardFilter);
}

function initializeStandardFilterValidation(standardFilter: JQuery<HTMLElement>)
{
    const preSaveErrorHelper: PreSaveValidationHelper = getPreSaveValidationHelper();
    preSaveErrorHelper.registerContainerValidation(standardFilter, `.${classNames.validationPrompt}.${classNames.filterValue}`, (fields: JQuery<HTMLElement>[]) =>
    {
        const promptValues = getCurrentPromptValues(standardFilter);

        return validatePrompt(fields, promptValues);
    });
}

function getCurrentPromptValues(standardFilter: JQuery<HTMLElement>): any
{
    const promptValues: Record<string, string> = {};

    standardFilter.find(`.${classNames.filterValue}`).each(function (this: HTMLElement)
    {
        const promptInput = $(this);
        const promptData = promptInput.data();
        const uniqueID: string = promptData.promptUniqueId;
        if (uniqueID)
        {
            const value = getValue(promptInput);
            promptValues[uniqueID] = value;
        }
    });

    return promptValues;
}

async function validatePrompt(promptElements: JQuery<HTMLElement>[], promptValues: any[]): Promise<ContainerPreSaveValidationResult>
{
    const results: ContainerPreSaveValidationResult =
    {
        validation: []
    };

    const validationResults = await performValidation(promptElements, promptValues);

    if (validationResults && validationResults.length > 0)
    {
        for (const validationResult of validationResults)
        {
            results.validation.push(validationResult);
        }
    }

    return results;
}

async function performValidation(promptElements: JQuery<HTMLElement>[], promptValues: any[]): Promise<PreSaveValidationResult[]>
{
    const promptElementsByPromptUniqueID = new Map<string, JQuery<HTMLElement>>();
    const promptUniqueIDs: PromptUniqueID[] = [];

    for (const prompt of promptElements)
    {
        const data = prompt.data();
        promptElementsByPromptUniqueID.set(data.promptUniqueId, prompt);

        const promptData: PromptUniqueID =
        {
            PromptUniqueID: data.promptUniqueId,
            PromptUniqueIDHash: data.promptUniqueIdHash
        };
        promptUniqueIDs.push(promptData);
    }

    const results: PerformValidationResult = await $.ajax(
    {
        url: `${virtualDir}SkySys/Prompt/ValidatePrompts`,
        type: "POST",
        data:
        {
            promptUniqueIDData: JSON.stringify(promptUniqueIDs),
            promptData: JSON.stringify(promptValues)
        },
        dataType: "JSON"
    });

    const validations: PreSaveValidationResult[] = [];
    for (const fieldValidation of results.validation)
    {
        const promptElement = promptElementsByPromptUniqueID.get(fieldValidation.PromptUniqueID);

        if (promptElement)
        {
            promptElementsByPromptUniqueID.delete(fieldValidation.PromptUniqueID);

            const fieldValidations: PreSaveValidation[] = [];
            for (const detail of fieldValidation.ValidationDetails)
            {

                const validation: PreSaveValidation =
                {
                    severityType: detail.SeverityType,
                    message: detail.Message
                };
                fieldValidations.push(validation);
            }

            const validationForField: PreSaveValidationResult =
            {
                showValidation: fieldValidations.length > 0,
                validation: fieldValidations,
                fieldElement: promptElement
            };

            validations.push(validationForField);
        }
    }

    for (const [_key, value] of promptElementsByPromptUniqueID)
    {
        const validationForField: PreSaveValidationResult =
        {
            showValidation: false,
            validation: [],
            fieldElement: value
        };
        validations.push(validationForField);
    }

    return validations;
}

function initializeAdvancedFilter(this: JQueryInput)
{
    const advancedFilter = $(this);
    advancedFilter.removeClass(classNames.advancedFilterUnbound);
    advancedFilter.on("change dirty", `.${classNames.filterViewValue}`, null, skyward.promptsview.setModifiedNotification);
    advancedFilter.each(skyward.promptsview.setModifiedNotification);
}

function addStandardFilterClick(this: JQueryInput)
{
    const collapsePanel = $(this).closest(`.${classNames.collapsePanel}`);
    const panelData = collapsePanel.data() as CollapsePanelData;
    const promptUniqueID = panelData.promptUniqueId ?? panelData.skywardId;
    const standardFilterViewer = getStandardFilterViewer(collapsePanel);
    const data = getDataFromStandardFilterBackingInput(standardFilterViewer);
    const viewerData = standardFilterViewer.data() as StandardFilterViewerData;
    const standardFilter = getStandardFilterByUniqueID(data.standardfilters, promptUniqueID)!;
    const newIndex = Math.max(...standardFilter.standardfilterindexes) + 1;
    const currentCount = standardFilter.standardfilterindexes.length;

    $.ajax({
        url: `${virtualDir}SkySys/StandardFilter/AddStandardFilter`,
        type: "POST",
        dataType: "JSON",
        success: async function (data)
        {
            const processedHtml = await processViewPartHtml(data.html);
            insertStandardFilter(collapsePanel, processedHtml, data.standardfilterdata, promptUniqueID, newIndex, panelData.idPrefix);
        },
        data:
        {
            currentCount: currentCount,
            filterIndex: newIndex,
            dataObjectType: standardFilter.type,
            skywardID: standardFilter.skywardid,
            relationshipPath: standardFilter.relationshippath,
            promptValuesJson: JSON.stringify(data.values.promptboundvalues),
            label: panelData.label,
            promptUniqueID: promptUniqueID,
            idPrefix: panelData.idPrefix,
            defaultPromptsJson: JSON.stringify(viewerData.defaultPrompts),
            readonlyIdsJson: JSON.stringify(viewerData.readonlyIds),
            isRequired: viewerData.isRequired,
            allowProcessingLists: standardFilter.allowprocessinglists
        }
    });
}

function removeStandardFilterClick(this: JQueryInput)
{
    removeCollapsePanel($(this).closest(`.${classNames.collapsePanel}`));
}

function removeCollapsePanel(collapsePanel: JQuery<HTMLElement>)
{
    const standardFilterViewer = getStandardFilterViewer(collapsePanel);
    const panelData = collapsePanel.data() as CollapsePanelData;
    const panelPromptUniqueID = panelData.promptUniqueId;
    const filterIndex = panelData.filterIndex;

    if (filterIndex <= 0)
    {
        return;
    }

    const promptUniqueIDsToRemove: string[] = [];
    collapsePanel.find(`.${classNames.filterValue}, .${classNames.filterProcessingListValue}`).each(function ()
    {
        const prompt = $(this);
        const promptData = prompt.data();
        const promptUniqueID = promptData.promptUniqueId;
        promptUniqueIDsToRemove.push(promptUniqueID);
    });

    const panelIsDirty = isDirty(collapsePanel);
    const isNewFilter = collapsePanel.hasClass(classNames.newFilter);
    if (panelIsDirty && isNewFilter)
    {
        collapsePanel.remove();
        triggerUpdateDirtyFilter(collapsePanel, standardFilterViewer);
    }
    else
    {
        collapsePanel.find(`.${classNames.filterValue}, .${classNames.filterProcessingListValue}`).each(function ()
        {
            triggerSavedFilterInputDirty($(this));
        });

        collapsePanel.remove();
    }

    const lastStandardFilterForUniqueID = standardFilterViewer.find(`.${classNames.collapsePanel}[data-prompt-unique-id='${panelPromptUniqueID}']`).last();
    const addButton = lastStandardFilterForUniqueID.find(`.${classNames.standardFilterAddButton}`);
    addButton.show();

    removeDataFromDataSource(standardFilterViewer, panelPromptUniqueID, filterIndex, promptUniqueIDsToRemove);
}

function getStandardFilterByUniqueID(standardFilters: StandardFilter[], uniqueID: string)
{
    return standardFilters.find(f => f.uniqueid == uniqueID);
}

function getStandardFilterIndexByUniqueID(standardFilters: StandardFilter[], uniqueID: string)
{
    return standardFilters.findIndex(f => f.uniqueid == uniqueID);
}

function getStandardFilterViewerBackingData(container: JQuery<HTMLElement>)
{
    return container.find(`.${classNames.dataSource}`);
}

function getDataFromStandardFilterBackingInput(container: JQuery<HTMLElement>)
{
    const backingData = getStandardFilterViewerBackingData(container);
    return JSON.parse(backingData.val() as string) as BackingInputData;
}

function getStandardFilterViewer(container: JQuery<HTMLElement>)
{
    return container.closest(`.${classNames.standardFilterViewer}`);
}

function getStandardFilterID(idPrefix: string, skywardId: string, uniqueId: string, index: number)
{
    let baseID = "standardfilter-" + skywardId + "-";
    if (uniqueId)
    {
        baseID = baseID + uniqueId + "-";
    }

    baseID = baseID + index;

    if (idPrefix)
    {
        return idPrefix + baseID;
    }

    return baseID;
}

function insertStandardFilter(collapsePanel: JQuery<HTMLElement>, html: JQueryInput, standardFilterData: string, uniqueId: string, newIndex: number, idPrefix: string)
{
    const newFilter = $(html);
    newFilter.insertAfter(collapsePanel);
    const panelContents = collapsePanel.find(`.${classNames.collapsePanelContents}`);
    const panelData = collapsePanel.data() as CollapsePanelData;

    const addButton = panelContents.find(`.${classNames.standardFilterAddButton}`);
    addButton.hide();

    const standardFilterViewer = getStandardFilterViewer(collapsePanel);
    const dataSource = standardFilterViewer.find(`.${classNames.dataSource}`);

    addDataToDataSource(standardFilterData, dataSource, uniqueId);

    notifyDOMLoad("#" + getStandardFilterID(idPrefix, panelData.skywardId, uniqueId, newIndex));

    newFilter.each(initializeStandardFilter);

    newFilter[0].scrollIntoView();
    newFilter.find(`.${classNames.filterValue}`).first().trigger("focus");

    newFilter.on("ready", function ()
    {
        collapsePanel.trigger("standardFilterAdd", [newFilter]);
    });
}

function addDataToDataSource(standardfilterdata: string, backingData: JQuery<HTMLElement>, promptUniqueID: string)
{
    const newData = JSON.parse(standardfilterdata);
    const data = JSON.parse(backingData.val() as string) as BackingInputData;
    const currentDataIndex = getStandardFilterIndexByUniqueID(data.standardfilters, promptUniqueID)!;
    const newDataIndex = getStandardFilterIndexByUniqueID(newData.standardfilters, promptUniqueID)!;
    data.standardfilters[currentDataIndex].standardfilterindexes = data.standardfilters[currentDataIndex].standardfilterindexes.concat(newData.standardfilters[newDataIndex].standardfilterindexes);
    $.extend(data.values.promptboundvalues, newData.values.promptboundvalues);
    backingData.val(JSON.stringify(data));
}

function removeDataFromDataSource(standardFilterViewer: JQuery<HTMLElement>, promptUniqueID: string, filterIndex: number, promptUniqueIDsToRemove: string[])
{
    const data = getDataFromStandardFilterBackingInput(standardFilterViewer);
    const backingData = getStandardFilterViewerBackingData(standardFilterViewer);

    for (const promptUniqueID of promptUniqueIDsToRemove)
    {
        if (data.values.promptboundvalues?.hasOwnProperty(promptUniqueID))
        {
            delete data.values.promptboundvalues[promptUniqueID];
        }
    }

    const standardFilter = getStandardFilterByUniqueID(data.standardfilters, promptUniqueID)!;
    standardFilter.standardfilterindexes = standardFilter.standardfilterindexes.filter(function (index) { return index != filterIndex; });
    if (standardFilter.standardfilterindexes.length <= 0)
    {
        standardFilter.standardfilterindexes = [1];
    }

    backingData.val(JSON.stringify(data));
}

function triggerUpdateDirtyFilter(collapsePanel: JQuery<HTMLElement>, standardFilterViewer: JQuery<HTMLElement>)
{
    const dirtyElements = collapsePanel.find("[data-dirty]");

    if (dirtyElements.length > 0)
    {
        $.each(dirtyElements, function ()
        {
            $(this).removeAttr("data-dirty");
        });
    }

    const filterInputToTrigger = standardFilterViewer.find(`.${classNames.filterValue}`).eq(0);
    const isWorkflow = standardFilterViewer.parent().hasClass(classNames.workflowContent);
    filterInputToTrigger.trigger(isWorkflow ? "update" : "change");
}

function triggerSavedFilterInputDirty(element: JQuery<HTMLElement>)
{
    const currentValue = element.val();
    if (element.is("[ignoredirtyfieldcomparison]") || currentValue == "" || currentValue == "{}" || currentValue == "[]")
    {
        return;
    }

    if (element.hasClass(classNames.comboBoxSet))
    {
        setValue(element, "", false);
    }
    else if (element.hasClass(classNames.multiSelectBackingData))
    {
        populate(element, [], true);
    }
    else if (element.hasClass(classNames.dmsBackingData))
    {
        setValue(element, "{}", false);
    }
    else
    {
        setValue(element, "true", false);
    }
}

function changeAdvancedFilterData(container: JQuery<HTMLElement>)
{
    const advancedFilter = container.find(`.${classNames.filterViewValue}`);
    const backingData = getStandardFilterViewerBackingData(container);
    const data = JSON.parse(backingData.val() as string) as BackingInputData;

    const advancedFilterData = advancedFilter.data("filterCondition");
    data.values.advancedfilter = advancedFilterData;
    backingData.val(JSON.stringify(data));
}

function changeFilterValue(originalFilter: JQueryInput, container: JQuery<HTMLElement>)
{
    const backingData = getStandardFilterViewerBackingData(container);
    const data = JSON.parse(backingData.val() as string) as BackingInputData;
    const filter = $(originalFilter);

    const uniqueId = filter.data("promptUniqueId").toLowerCase();
    data.values.promptboundvalues = data.values.promptboundvalues || {};
    const filterValue = getUnencodedValue(filter);

    if (skyward.promptsview.isNonEmptyPromptValue(filterValue))
    {
        data.values.promptboundvalues[uniqueId] = filterValue;
    }
    else
    {
        delete data.values.promptboundvalues[uniqueId];
    }
    backingData.val(JSON.stringify(data));
}